package ru.chm.gradereport.ui.screens.course.sheet

import ru.chm.gradereport.model.AttendanceStatus
import ru.chm.gradereport.model.GradeSheetDto
import ru.chm.gradereport.model.StudentDto
import kotlin.math.roundToInt

internal sealed interface GradeFilter {
    data object All : GradeFilter
    data object HasAny : GradeFilter
    data object Missing : GradeFilter
    data class Score(val value: Int) : GradeFilter
}

internal sealed interface AttendanceFilter {
    data object All : AttendanceFilter
    data object Missing : AttendanceFilter
    data class Status(val status: AttendanceStatus) : AttendanceFilter
}

/** Quick grade-control filters over unseen submissions and missing homework. */
internal sealed interface AttentionFilter {
    data object All : AttentionFilter

    /** Students with at least one unseen submission. */
    data object UnseenSubmissions : AttentionFilter

    /** Students with no unseen submissions and no grades — did not submit homework. */
    data object NotSubmitted : AttentionFilter
}

internal data class SheetFilterState(
    val nameQuery: String = "",
    val grade: GradeFilter = GradeFilter.All,
    val attendance: AttendanceFilter = AttendanceFilter.All,
    val attention: AttentionFilter = AttentionFilter.All,
) {
    val isActive: Boolean
        get() = nameQuery.isNotBlank() ||
            grade != GradeFilter.All ||
            attendance != AttendanceFilter.All ||
            attention != AttentionFilter.All
}

/**
 * @param focusDate if set, grade/attendance filters apply only to sessions (and their ДЗ) on that date.
 */
internal fun filterStudents(
    sheet: GradeSheetDto,
    filters: SheetFilterState,
    gradeScores: Map<Pair<String, String>, Double?>,
    attendanceStatuses: Map<Pair<String, String>, AttendanceStatus?>,
    focusDate: String? = null,
): List<StudentDto> {
    val query = filters.nameQuery.trim()
    val scopedSessions = if (focusDate == null) {
        sheet.sessions
    } else {
        sheet.sessions.filter { it.date == focusDate }
    }
    val sessionIds = scopedSessions.map { it.id }
    val sessionIdSet = sessionIds.toSet()
    val homeworkIds = if (focusDate == null) {
        sheet.homeworks.map { it.id }
    } else {
        sheet.homeworks
            .filter { hw -> hw.sessionId != null && hw.sessionId in sessionIdSet }
            .map { it.id }
    }
    val homeworkIdSet = homeworkIds.toSet()
    val unseenSubmissionStudentIds = sheet.submissions
        .filter { !it.seen && it.homeworkId in homeworkIdSet }
        .mapTo(mutableSetOf()) { it.studentId }

    return sheet.students.filter { student ->
        if (query.isNotBlank()) {
            if (!student.fullName.contains(query, ignoreCase = true)) return@filter false
        }

        val scores = homeworkIds.map { hwId -> gradeScores[student.id to hwId] }
        when (val g = filters.grade) {
            GradeFilter.All -> Unit
            GradeFilter.HasAny -> if (scores.none { it != null }) return@filter false
            GradeFilter.Missing -> {
                if (homeworkIds.isEmpty()) return@filter false
                if (scores.all { it != null }) return@filter false
            }
            is GradeFilter.Score -> {
                if (scores.none { it?.roundToInt() == g.value }) return@filter false
            }
        }

        when (val a = filters.attendance) {
            AttendanceFilter.All -> Unit
            AttendanceFilter.Missing -> {
                if (sessionIds.isEmpty()) return@filter false
                val hasMissing = sessionIds.any { sessionId ->
                    isUnmarkedAttendance(attendanceStatuses[student.id to sessionId])
                }
                if (!hasMissing) return@filter false
            }
            is AttendanceFilter.Status -> {
                if (sessionIds.isEmpty()) return@filter false
                val hasStatus = sessionIds.any { sessionId ->
                    attendanceStatuses[student.id to sessionId] == a.status
                }
                if (!hasStatus) return@filter false
            }
        }

        when (filters.attention) {
            AttentionFilter.All -> Unit
            AttentionFilter.UnseenSubmissions -> {
                if (student.id !in unseenSubmissionStudentIds) return@filter false
            }
            AttentionFilter.NotSubmitted -> {
                if (homeworkIds.isEmpty()) return@filter false
                val hasUnseen = student.id in unseenSubmissionStudentIds
                val hasGrade = homeworkIds.any { hwId ->
                    gradeScores[student.id to hwId] != null
                }
                if (hasUnseen || hasGrade) return@filter false
            }
        }

        true
    }.sortedWith(compareBy(String.CASE_INSENSITIVE_ORDER) { it.fullName })
}

internal fun sheetMaxGradeOption(sheet: GradeSheetDto, focusDate: String? = null): Int {
    val homeworks = if (focusDate == null) {
        sheet.homeworks
    } else {
        val sessionIds = sheet.sessions.filter { it.date == focusDate }.map { it.id }.toSet()
        sheet.homeworks.filter { hw -> hw.sessionId != null && hw.sessionId in sessionIds }
    }
    return homeworks.maxOfOrNull { it.maxScore.roundToInt() }?.coerceAtLeast(1) ?: 5
}
